/**
 * PQS score-tier boundaries — the single source of truth shared by the
 * placements API (tier filtering → pqsScore range where-clauses) and the
 * web UI (tier badges via pqsTierFromScore). Pure constants + functions,
 * browser-safe.
 */

export type PqsTierKey = "premium" | "strong" | "moderate" | "limited";

export interface PqsTierRange {
  key: PqsTierKey;
  tier: 1 | 2 | 3 | 4;
  label: string;
  /** Inclusive lower bound. */
  min: number;
  /** Exclusive upper bound; null = unbounded (top tier). */
  maxExclusive: number | null;
}

export const PQS_TIER_RANGES: readonly PqsTierRange[] = [
  { key: "premium", tier: 1, label: "Premium", min: 75, maxExclusive: null },
  { key: "strong", tier: 2, label: "Strong", min: 50, maxExclusive: 75 },
  { key: "moderate", tier: 3, label: "Moderate", min: 25, maxExclusive: 50 },
  { key: "limited", tier: 4, label: "Limited", min: 0, maxExclusive: 25 },
] as const;

export function pqsTierFromScore(score: number | null): {
  tier: 1 | 2 | 3 | 4 | null;
  label: string;
} {
  if (score == null) return { tier: null, label: "Pending" };
  // Ranges are ordered by descending min — first match wins.
  for (const range of PQS_TIER_RANGES) {
    if (score >= range.min) return { tier: range.tier, label: range.label };
  }
  // Scores below 0 shouldn't exist; treat as the bottom tier.
  return { tier: 4, label: "Limited" };
}

export function pqsTierRangeFor(key: string): PqsTierRange | undefined {
  return PQS_TIER_RANGES.find((r) => r.key === key);
}
